import { useState } from 'react'
import type { ReactElement } from 'react'

import { useSession } from '../../auth/useSession'
import {
  Button,
  CodeLabel,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  MachinePicker,
  Modal,
  Select,
  Spinner,
  Table,
  Tag,
} from '../../components'
import type { TableColumn } from '../../components'
import { FreshnessBadge } from '../../layout/FreshnessBadge'
import { trpcMessage } from '../../lib/trpcMessages'
import { MIRROR_REFETCH_MS, trpc } from '../../trpc'
import type { RouterOutputs } from '../../trpc'
import { AdminPageHeader, ReadOnlyNote, adminStyles as styles } from '../admin/AdminPage'
import { PLANNING_GROUP_OPTIONS, isPlanningGroup } from './planning'
import type { PlanningGroup } from './planning'
import { useConfigurationRefresh } from './useConfigurationRefresh'

type MachineGroupRow = RouterOutputs['configuration']['listMachineGroups']['machines'][number]

/** The select's value for "no group": clearing the row, not a sixth group. */
const NO_GROUP = ''

const GROUP_SELECT_OPTIONS = [{ value: NO_GROUP, label: 'Sin grupo' }, ...PLANNING_GROUP_OPTIONS]

/**
 * GRUPO: which planning group each machine of the mirror is planned in. A
 * machine with no group leaves its operations unplanned, with a warning, so
 * the list can be narrowed to those. Codes `app` holds that the mirror no
 * longer has are listed apart, to be cleared.
 */
export const MachineGroupsPage = (): ReactElement => {
  const { isReadOnly } = useSession()
  const utils = trpc.useUtils()
  const refreshStatus = useConfigurationRefresh()

  const [onlyWithoutGroup, setOnlyWithoutGroup] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [clearing, setClearing] = useState<MachineGroupRow | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const groupsQuery = trpc.configuration.listMachineGroups.useQuery(undefined, { refetchInterval: MIRROR_REFETCH_MS })

  const afterWrite = (): void => {
    setActionError(null)
    void utils.configuration.listMachineGroups.invalidate()
    refreshStatus()
  }

  const setGroup = trpc.configuration.setMachineGroup.useMutation({
    onSuccess: afterWrite,
    onError: (error) => setActionError(trpcMessage(error)),
  })
  const clearGroup = trpc.configuration.clearMachineGroup.useMutation({
    onSuccess: () => {
      setClearing(null)
      afterWrite()
    },
    onError: (error) => {
      setClearing(null)
      setActionError(trpcMessage(error))
    },
  })
  const saving = setGroup.isPending || clearGroup.isPending

  const changeGroup = (row: MachineGroupRow, value: string): void => {
    setActionError(null)
    if (value === NO_GROUP) {
      if (row.planningGroup !== null) {
        clearGroup.mutate({ workstationCode: row.workstationCode })
      }
    } else if (isPlanningGroup(value) && value !== row.planningGroup) {
      setGroup.mutate({ workstationCode: row.workstationCode, planningGroup: value })
    }
  }

  const machineColumns: Array<TableColumn<MachineGroupRow>> = [
    {
      key: 'machine',
      header: 'Máquina',
      render: (row) => <CodeLabel code={row.workstationCode} title={row.title} known={row.known} />,
    },
    {
      key: 'type',
      header: 'Tipo de estación',
      render: (row) => row.typeTitle ?? <span className={styles['admin-page__muted']}>—</span>,
      hideOnMobile: true,
    },
    {
      key: 'group',
      header: 'Grupo',
      render: (row) => (
        <Select
          label={`Grupo de ${row.workstationCode}`}
          hideLabel
          options={GROUP_SELECT_OPTIONS}
          value={row.planningGroup ?? NO_GROUP}
          disabled={isReadOnly || saving}
          onChange={(event) => changeGroup(row, event.target.value)}
        />
      ),
    },
  ]

  const unusedColumns: Array<TableColumn<MachineGroupRow>> = [
    {
      key: 'machine',
      header: 'Código',
      render: (row) => <CodeLabel code={row.workstationCode} title={null} known />,
    },
    { key: 'type', header: 'Tipo', render: (row) => row.typeTitle ?? 'Tipo desconocido' },
    { key: 'group', header: 'Grupo', render: (row) => row.planningGroup ?? '—' },
    {
      key: 'actions',
      header: 'Acciones',
      align: 'right',
      render: (row) => (
        <Button
          variant="danger"
          disabled={isReadOnly || saving}
          onClick={() => setClearing(row)}
          aria-label={`Quitar el grupo de ${row.workstationCode}`}
          label="Quitar"
        />
      ),
    },
  ]

  let body: ReactElement
  if (groupsQuery.isPending) {
    body = <Spinner label="Cargando máquinas…" />
  } else if (groupsQuery.isError) {
    body = <ErrorState error={groupsQuery.error} onRetry={() => void groupsQuery.refetch()} />
  } else {
    const known = groupsQuery.data.machines.filter((row) => row.known)
    const unused = groupsQuery.data.machines.filter((row) => !row.known)
    const shown = onlyWithoutGroup ? known.filter((row) => row.planningGroup === null) : known
    const withoutGroup = known.filter((row) => row.planningGroup === null).length
    body = (
      <>
        <div className={styles['admin-page__actions']}>
          <FreshnessBadge freshness={groupsQuery.data.freshness} />
          {withoutGroup > 0 && <Tag tone="warn" label={`${withoutGroup} sin grupo`} />}
        </div>
        {shown.length === 0
          ? (
            <EmptyState
              title={onlyWithoutGroup ? 'Todas las máquinas tienen grupo' : 'El espejo no tiene máquinas'}
              message={onlyWithoutGroup
                ? undefined
                : 'Una máquina aparece en el espejo cuando se usa en alguna orden de fabricación.'}
            />
          )
          : <Table columns={machineColumns} rows={shown} rowKey={(row) => row.workstationCode} caption="Máquinas del espejo y su grupo" />}

        {unused.length > 0 && (
          <section className={styles['admin-page__section']}>
            <h2 className={styles['admin-page__section-title']}>Máquinas sin uso en las órdenes recientes</h2>
            <p className={styles['admin-page__intro']}>
              MRPeasy no da la lista de máquinas: el espejo solo ve las que usa alguna de las órdenes que tiene
              cargadas, que son las de los últimos días. Estas tienen grupo pero no se han usado en esas órdenes.
              Lo normal es que sigan existiendo en MRPeasy (una marmita nocturna, un horno de fin de semana).
            </p>
            <p className={styles['admin-page__intro']}>
              Quita el grupo solo si la máquina ya no existe en MRPeasy: si se vuelve a usar sin grupo, el
              planificador deja sus operaciones sin planificar.
            </p>
            <Table columns={unusedColumns} rows={unused} rowKey={(row) => row.workstationCode} caption="Máquinas sin uso en las órdenes recientes" />
          </section>
        )}
      </>
    )
  }

  return (
    <section className={styles['admin-page']}>
      <AdminPageHeader
        title="Máquinas → grupo"
        intro="En qué grupo de planificación (Olla, Horno, CP, Manual o Remojo) se planifica cada máquina. Una máquina sin grupo deja sus operaciones sin planificar."
        actions={<Button disabled={isReadOnly} onClick={() => setAssigning(true)} label="Asignar grupo" />}
      />
      {isReadOnly && <ReadOnlyNote />}

      <label className={styles['admin-page__toggle']}>
        <input
          type="checkbox"
          className={styles['admin-page__checkbox']}
          checked={onlyWithoutGroup}
          onChange={(event) => setOnlyWithoutGroup(event.target.checked)}
        />
        Solo sin grupo
      </label>

      {actionError !== null && <p role="alert" className={styles['admin-page__error']}>{actionError}</p>}

      {body}

      {assigning && (
        <AssignGroupDialog
          onClose={() => setAssigning(false)}
          onSaved={() => {
            setAssigning(false)
            afterWrite()
          }}
        />
      )}

      <ConfirmDialog
        open={clearing !== null}
        variant="danger"
        title="Quitar el grupo"
        message={clearing === null
          ? ''
          : `${clearing.workstationCode} dejará de tener grupo: si una orden la usa, el planificador dejará esa operación sin planificar.`}
        confirmLabel="Quitar"
        isLoading={clearGroup.isPending}
        onCancel={() => setClearing(null)}
        onConfirm={() => {
          if (clearing !== null) {
            clearGroup.mutate({ workstationCode: clearing.workstationCode })
          }
        }}
      />
    </section>
  )
}

type AssignGroupDialogProps = {
  onClose: () => void
  onSaved: () => void
}

/** A machine chosen from the mirror's list and its group: the long table, without scrolling it. */
const AssignGroupDialog = ({ onClose, onSaved }: AssignGroupDialogProps): ReactElement => {
  const [workstationCode, setWorkstationCode] = useState('')
  const [planningGroup, setPlanningGroup] = useState<PlanningGroup | ''>('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const setGroup = trpc.configuration.setMachineGroup.useMutation({
    onSuccess: onSaved,
    onError: (error) => setErrorMessage(trpcMessage(error)),
  })

  return (
    <Modal open title="Asignar grupo a una máquina" onClose={onClose} maxWidth="56rem">
      <form
        className={styles['admin-page__form']}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          if (workstationCode !== '' && planningGroup !== '') {
            setErrorMessage(null)
            setGroup.mutate({ workstationCode, planningGroup })
          }
        }}
      >
        <MachinePicker label="Máquina" value={workstationCode} onChange={setWorkstationCode} />
        <Select
          label="Grupo de planificación"
          placeholder="Elige un grupo…"
          options={PLANNING_GROUP_OPTIONS}
          value={planningGroup}
          onChange={(event) => {
            const { value } = event.target
            setPlanningGroup(isPlanningGroup(value) ? value : '')
          }}
        />
        {errorMessage !== null && <p role="alert" className={styles['admin-page__error']}>{errorMessage}</p>}
        <div className={styles['admin-page__form-actions']}>
          <Button variant="secondary" onClick={onClose} disabled={setGroup.isPending} label="Cancelar" />
          <Button
            type="submit"
            disabled={setGroup.isPending || workstationCode === '' || planningGroup === ''}
            label="Guardar"
          />
        </div>
      </form>
    </Modal>
  )
}
